import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Loader2, Users, Compass, Target } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { usePageContent } from "../lib/usePageContent";
import { useLivePricing } from "../lib/useLivePricing";
import TestimonialsSection from "@/components/site/TestimonialsSection";
import SEO, { serviceSchema } from "@/components/site/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INCLUSIONS = [
    { icon: Compass, title: "Facilitated Interpretation", body: "We walk your leadership team through their Snapshot results — pillar by pillar." },
    { icon: Users, title: "Executive Alignment", body: "Structured discussion that surfaces the two or three priorities your team must own." },
    { icon: Target, title: "30-Day Action Plan", body: "Leave with a written plan your team built together — not a deck of ideas." },
];

const currency = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const Workshop = () => {
    const navigate = useNavigate();
    const { get } = usePageContent("workshop");
    const { byProduct, loaded: pricingLoaded } = useLivePricing();
    const workshops = byProduct("workshop");
    const [selectedTier, setSelectedTier] = React.useState("individual");
    const selectedRow = workshops.find((w) => w.tier_key === selectedTier);
    const isCustomQuote = !!selectedRow?.is_custom_quote;
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm();

    const [done, setDone] = React.useState(false);
    const [paymentState, setPaymentState] = React.useState({
        status: "idle",
        amount: null,
        currency: null,
        package_name: null,
    });

    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get("session_id");
        const payment = params.get("payment");
        if (payment === "cancelled") {
            setPaymentState((s) => ({ ...s, status: "cancelled" }));
            toast.error("Payment cancelled. You can retry anytime.");
            window.history.replaceState({}, "", window.location.pathname);
            return;
        }
        if (!sessionId) return;
        setPaymentState((s) => ({ ...s, status: "polling" }));
        setDone(true);
        window.scrollTo({ top: 0, behavior: "smooth" });

        let attempts = 0;
        const poll = async () => {
            try {
                const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setPaymentState({
                        status: "paid",
                        amount: data.amount,
                        currency: data.currency,
                        package_name: data.package_name,
                    });
                    toast.success("Payment received. You're registered.");
                    window.history.replaceState({}, "", window.location.pathname);
                    return;
                }
                if (data.status === "expired") {
                    setPaymentState((s) => ({ ...s, status: "failed" }));
                    return;
                }
                if (attempts < 6) {
                    attempts += 1;
                    setTimeout(poll, 2000);
                } else {
                    setPaymentState((s) => ({ ...s, status: "failed" }));
                }
            } catch {
                setPaymentState((s) => ({ ...s, status: "failed" }));
            }
        };
        poll();
    }, []);

    const onSubmit = async (data) => {
        // Custom-quote tier: skip Stripe, redirect to Contact with prefilled subject.
        if (isCustomQuote) {
            try {
                await axios.post(`${API}/workshop/register`, {
                    ...data,
                    tier_key: selectedTier,
                    // no origin_url → server won't create a checkout session
                });
            } catch { /* keep going — the contact flow still runs */ }
            toast.success("Request received. Redirecting to Contact so our team can quote your cohort.");
            navigate(`/contact?subject=${encodeURIComponent("Private Workshop pricing — cohort of 16+")}`);
            return;
        }
        try {
            const res = await axios.post(`${API}/workshop/register`, {
                ...data,
                tier_key: selectedTier,
                origin_url: window.location.origin,
            });
            const checkoutUrl = res.data?.checkout?.url;
            if (checkoutUrl) {
                window.location.href = checkoutUrl;
                return;
            }
            setDone(true);
            toast.success("Registration received.");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch {
            toast.error("Could not submit your registration. Please try again.");
        }
    };

    return (
        <div>
            <SEO
                title="HUMAN+ Snapshot Workshop"
                description="A guided session that unpacks your HUMAN+ Snapshot results, aligns leadership on priorities, and produces a written 30-day action plan."
                path="/workshop"
                schema={serviceSchema({
                    name: "HUMAN+ Snapshot Workshop",
                    description: "Facilitated executive-team workshop on the HUMAN+ Snapshot results.",
                    price: 249,
                })}
            />
            {/* Hero */}
            <section className="bg-indigo_brand text-white">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 sm:py-28">
                    <p className="font-poppins text-[11px] font-semibold uppercase tracking-[0.24em] text-green_brand">
                        {get("hero.eyebrow", "Snapshot Workshop")}
                    </p>
                    <h1 className="mt-4 font-montserrat font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                        {get("hero.title", "Turn the HUMAN+ Snapshot™ into an executive-team conversation.")}
                    </h1>
                    <p className="mt-6 font-opensans text-lg sm:text-xl text-white/85 leading-relaxed max-w-3xl">
                        {get("hero.subtitle", "A guided session that unpacks your Snapshot results, aligns leadership on priorities, and produces a written action plan you can execute the next day.")}
                    </p>
                </div>
            </section>

            {/* What's included */}
            <section className="py-20 sm:py-28 bg-white">
                <div className="max-w-6xl mx-auto px-6 sm:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {INCLUSIONS.map((it) => (
                            <div key={it.title} className="ih-card p-8 shadow-medium">
                                <span className="inline-flex w-12 h-12 items-center justify-center rounded-xl bg-green_brand/15">
                                    <it.icon size={22} className="text-green_brand" />
                                </span>
                                <h3 className="mt-5 font-montserrat font-bold text-indigo_brand text-lg">{it.title}</h3>
                                <p className="mt-2 font-opensans text-[#4A5568] text-sm leading-relaxed">{it.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Registration + tier picker */}
            <section className="py-20 sm:py-28 bg-[#F5F5F5]">
                <div className="max-w-4xl mx-auto px-6 sm:px-8">
                    {done ? (
                        <div data-testid="workshop-thanks" className="ih-card p-10 shadow-medium">
                            <span className="inline-flex w-12 h-12 items-center justify-center rounded-xl bg-green_brand/15">
                                <Check size={22} className="text-green_brand" />
                            </span>
                            {paymentState.status === "paid" ? (
                                <>
                                    <h3 className="mt-5 font-montserrat font-bold text-indigo_brand text-2xl" data-testid="workshop-paid-headline">
                                        You&rsquo;re registered.
                                    </h3>
                                    <p className="mt-3 font-opensans text-[#4A5568] text-base leading-relaxed">
                                        We&rsquo;ve received <strong className="text-indigo_brand">
                                        ${paymentState.amount ? paymentState.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ""} {paymentState.currency ? paymentState.currency.toUpperCase() : ""}
                                        </strong> for the {paymentState.package_name}. A confirmation receipt is on its way with next-step details.
                                    </p>
                                </>
                            ) : paymentState.status === "polling" ? (
                                <>
                                    <h3 className="mt-5 font-montserrat font-bold text-indigo_brand text-2xl">Finalizing your payment&hellip;</h3>
                                    <p className="mt-3 font-opensans text-[#4A5568] text-base">This usually takes a few seconds.</p>
                                </>
                            ) : (
                                <>
                                    <h3 className="mt-5 font-montserrat font-bold text-indigo_brand text-2xl">Registration received.</h3>
                                    <p className="mt-3 font-opensans text-[#4A5568] text-base">A confirmation is on its way to your inbox.</p>
                                </>
                            )}
                            <Link to="/" className="ih-btn-secondary mt-7" data-testid="workshop-return-home">Return Home</Link>
                        </div>
                    ) : (
                        <>
                            <div className="max-w-2xl mb-10">
                                <p className="font-poppins text-[11px] font-semibold uppercase tracking-[0.24em] text-green_brand">Investment</p>
                                <h2 className="mt-3 font-montserrat font-bold text-indigo_brand text-3xl sm:text-4xl">Choose your workshop.</h2>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
                                {workshops.map((w) => (
                                    <button
                                        key={w.tier_key}
                                        type="button"
                                        data-testid={`workshop-tier-${w.tier_key}`}
                                        onClick={() => setSelectedTier(w.tier_key)}
                                        className={`text-left ih-card p-7 border-2 transition ${
                                            selectedTier === w.tier_key
                                                ? "border-green_brand shadow-medium"
                                                : "border-transparent hover:border-[#EDEFF3]"
                                        }`}
                                    >
                                        <div className="flex items-baseline justify-between gap-4">
                                            <span className="font-montserrat font-bold text-indigo_brand text-xl">{w.label}</span>
                                            {w.is_custom_quote ? (
                                                <span className="font-poppins text-[11px] font-semibold uppercase tracking-[0.16em] text-green_brand shrink-0">
                                                    Contact for pricing
                                                </span>
                                            ) : (
                                                <span className="font-montserrat font-bold text-indigo_brand text-2xl">{currency(w.amount_usd)}</span>
                                            )}
                                        </div>
                                        <p className="mt-2 font-opensans text-[#4A5568] text-sm">{w.description}</p>
                                    </button>
                                ))}
                                {!pricingLoaded && (
                                    <p className="col-span-full font-opensans text-[#4A5568] text-sm">Loading pricing…</p>
                                )}
                            </div>

                            <form data-testid="workshop-form" onSubmit={handleSubmit(onSubmit)} className="ih-card p-8 sm:p-10 shadow-medium grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }} aria-hidden>
                                    <label>Company website<input type="text" tabIndex={-1} autoComplete="off" {...register("hp_field")} /></label>
                                </div>
                                <div>
                                    <Label>First Name *</Label>
                                    <Input {...register("first_name", { required: true })} data-testid="ws-first-name" />
                                    {errors.first_name && <p className="mt-1 text-xs text-red-600">Required.</p>}
                                </div>
                                <div>
                                    <Label>Last Name *</Label>
                                    <Input {...register("last_name", { required: true })} data-testid="ws-last-name" />
                                    {errors.last_name && <p className="mt-1 text-xs text-red-600">Required.</p>}
                                </div>
                                <div className="sm:col-span-2">
                                    <Label>Work Email *</Label>
                                    <Input type="email" {...register("email", { required: true })} data-testid="ws-email" />
                                </div>
                                <div className="sm:col-span-2">
                                    <Label>Organization</Label>
                                    <Input {...register("organization_name")} data-testid="ws-org" />
                                </div>
                                <div className="sm:col-span-2">
                                    <Label>Notes</Label>
                                    <Textarea rows={4} {...register("notes")} data-testid="ws-notes" placeholder="Anything we should know?" />
                                </div>
                                <div className="sm:col-span-2 pt-2 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                                    <p className="font-opensans text-xs text-[#4A5568]">
                                        {isCustomQuote
                                            ? "For cohorts of 16+, we'll follow up with a tailored quote."
                                            : "You'll be redirected to secure checkout."}
                                    </p>
                                    <button type="submit" disabled={isSubmitting} data-testid="workshop-submit" className="ih-btn-primary disabled:opacity-60">
                                        {isSubmitting ? (
                                            <><Loader2 className="animate-spin" size={16} /> {isCustomQuote ? "Sending…" : "Redirecting to payment…"}</>
                                        ) : isCustomQuote ? (
                                            <>Request a Quote <ArrowRight size={16} /></>
                                        ) : (
                                            <>Continue to Payment <ArrowRight size={16} /></>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </section>

            <TestimonialsSection page="workshop" />
        </div>
    );
};

export default Workshop;
